import { listedConversationRecords } from 'dashboard/permissionSets/conversationReads';
import { captureNotificationResponse } from 'dashboard/permissionSets/notificationResponses';
import types from '../../mutation-types';
import NotificationsAPI from '../../../api/notifications';

// Notifications are list-scoped: those of a conversation that lost access
// after the read are dropped.
const withoutRevokedActors = (context, notifications) =>
  listedConversationRecords(
    context.rootState,
    notifications,
    notification => notification.primary_actor?.id
  );

// A page read keeps the unread total it carries only while no newer read of
// the total (page or unread count) was issued.
const withUnreadTotal = (context, meta, unreadTotalIsCurrent) =>
  unreadTotalIsCurrent()
    ? meta
    : { ...meta, unread_count: context.state.meta.unreadCount };

// A total computed by a write from the one shown when it started: if a newer
// read of the total was issued meanwhile, the count is read again instead.
const setUnreadTotal = (context, unreadTotalIsCurrent, total) => {
  if (unreadTotalIsCurrent())
    context.commit(types.SET_NOTIFICATIONS_UNREAD_COUNT, total);
  else context.dispatch('unReadCount');
};

export const actions = {
  get: async (context, { page = 1 } = {}) => {
    const { commit, unreadTotalIsCurrent } = captureNotificationResponse(
      context,
      'list',
      page
    );
    commit(types.SET_NOTIFICATIONS_UI_FLAG, { isFetching: true });
    try {
      const {
        data: {
          data: { payload, meta },
        },
      } = await NotificationsAPI.get({ page });
      commit(types.CLEAR_NOTIFICATIONS);
      commit(types.SET_NOTIFICATIONS, withoutRevokedActors(context, payload));
      commit(
        types.SET_NOTIFICATIONS_META,
        withUnreadTotal(context, meta, unreadTotalIsCurrent)
      );
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isFetching: false });
    }
  },
  index: async (context, { page = 1, status, type, sortOrder } = {}) => {
    const { commit, unreadTotalIsCurrent } = captureNotificationResponse(
      context,
      'list'
    );
    commit(types.SET_NOTIFICATIONS_UI_FLAG, { isFetching: true });
    try {
      const {
        data: {
          data: { payload, meta },
        },
      } = await NotificationsAPI.get({
        page,
        status,
        type,
        sortOrder,
      });
      commit(types.SET_NOTIFICATIONS, withoutRevokedActors(context, payload));
      commit(
        types.SET_NOTIFICATIONS_META,
        withUnreadTotal(context, meta, unreadTotalIsCurrent)
      );
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isFetching: false });
      if (payload.length < 15) {
        commit(types.SET_ALL_NOTIFICATIONS_LOADED);
      }
    } catch (error) {
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isFetching: false });
    }
  },
  unReadCount: async context => {
    const { commit, unreadTotalIsCurrent } = captureNotificationResponse(
      context,
      'unReadCount'
    );
    commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdatingUnreadCount: true });
    try {
      const { data } = await NotificationsAPI.getUnreadCount();
      if (unreadTotalIsCurrent())
        commit(types.SET_NOTIFICATIONS_UNREAD_COUNT, data);
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdatingUnreadCount: false });
    } catch (error) {
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdatingUnreadCount: false });
    }
  },
  read: async (
    context,
    { id, primaryActorType, primaryActorId, unreadCount }
  ) => {
    const { commit, unreadTotalIsCurrent } =
      captureNotificationResponse(context);
    commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdating: true });
    try {
      await NotificationsAPI.read(primaryActorType, primaryActorId);
      setUnreadTotal(context, unreadTotalIsCurrent, unreadCount - 1);
      commit(types.READ_NOTIFICATION, { id, read_at: new Date() });
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdating: false });
    }
  },
  unread: async (context, { id }) => {
    const { commit } = captureNotificationResponse(context);
    commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdating: true });
    try {
      await NotificationsAPI.unRead(id);
      commit(types.READ_NOTIFICATION, { id, read_at: null });
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdating: false });
    }
  },
  readAll: async context => {
    const { commit, isCurrent, unreadTotalIsCurrent } =
      captureNotificationResponse(context);
    commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdating: true });
    try {
      await NotificationsAPI.readAll();
      setUnreadTotal(context, unreadTotalIsCurrent, 0);
      commit(types.UPDATE_ALL_NOTIFICATIONS);
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdating: false });
      if (isCurrent()) throw new Error(error);
    }
  },

  delete: async (context, { notification, count, unreadCount }) => {
    const { commit, unreadTotalIsCurrent } =
      captureNotificationResponse(context);
    commit(types.SET_NOTIFICATIONS_UI_FLAG, { isDeleting: true });
    try {
      await NotificationsAPI.delete(notification.id);
      const totalsAreCurrent = unreadTotalIsCurrent();
      setUnreadTotal(context, unreadTotalIsCurrent, unreadCount - 1);
      // After a newer read of the totals, only the record goes.
      commit(
        types.DELETE_NOTIFICATION,
        totalsAreCurrent
          ? { notification, count, unreadCount }
          : {
              notification,
              count: context.state.meta.count,
              unread_count: context.state.meta.unreadCount,
            }
      );
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isDeleting: false });
    }
  },

  deleteAllRead: async context => {
    const { commit } = captureNotificationResponse(context);
    commit(types.SET_NOTIFICATIONS_UI_FLAG, { isDeleting: true });
    try {
      await NotificationsAPI.deleteAll({
        type: 'read',
      });
      commit(types.DELETE_READ_NOTIFICATIONS);
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isDeleting: false });
    }
  },
  deleteAll: async context => {
    const { commit } = captureNotificationResponse(context);
    commit(types.SET_NOTIFICATIONS_UI_FLAG, { isDeleting: true });
    try {
      await NotificationsAPI.deleteAll({
        type: 'all',
      });
      commit(types.DELETE_ALL_NOTIFICATIONS);
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isDeleting: false });
    }
  },

  snooze: async (context, { id, snoozedUntil }) => {
    const { commit } = captureNotificationResponse(context);
    commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdating: true });
    try {
      const response = await NotificationsAPI.snooze({
        id,
        snoozedUntil,
      });

      const {
        data: { snoozed_until = null },
      } = response;
      commit(types.SNOOZE_NOTIFICATION, {
        id,
        snoozed_until,
      });
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_NOTIFICATIONS_UI_FLAG, { isUpdating: false });
    }
  },

  updateNotification: async ({ commit }, data) => {
    commit(types.UPDATE_NOTIFICATION, data);
  },

  addNotification({ commit }, data) {
    commit(types.ADD_NOTIFICATION, data);
  },
  deleteNotification({ commit }, data) {
    commit(types.DELETE_NOTIFICATION, data);
  },
  clear({ commit }) {
    commit(types.CLEAR_NOTIFICATIONS);
  },

  setNotificationFilters: ({ commit }, filters) => {
    commit(types.SET_NOTIFICATION_FILTERS, filters);
  },
  updateNotificationFilters: ({ commit }, filters) => {
    commit(types.UPDATE_NOTIFICATION_FILTERS, filters);
  },
};
